import { useEffect, useRef, useCallback } from "react";

interface UseDocumentProcessingPollingOptions {
  /** Documents to check for processing status */
  documents: Array<{ status: string }> | undefined;
  /** Callback to refetch data when polling */
  onRefetch: () => void;
  /** Polling interval in milliseconds (default: 3000) */
  pollingInterval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
}

// Processing statuses - documents in these states need polling
const PROCESSING_STATUSES = ["UPLOADED", "TEXT_EXTRACTED", "KEYS_EXTRACTED"];

/**
 * Hook to poll for document processing status updates.
 * Automatically polls while documents are in processing states and stops
 * when all documents reach terminal states (READY or FAILED).
 */
const useDocumentProcessingPolling = ({
  documents,
  onRefetch,
  pollingInterval = 3000,
  enabled = true,
}: UseDocumentProcessingPollingOptions) => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);

  // Check if any documents are still processing
  const hasProcessingDocuments = useCallback(() => {
    if (!documents || documents.length === 0) return false;
    return documents.some((doc) => PROCESSING_STATUSES.includes(doc.status));
  }, [documents]);

  // Get counts for UI display
  const getProcessingCounts = useCallback(() => {
    if (!documents || documents.length === 0) {
      return { processing: 0, ready: 0, failed: 0, total: 0 };
    }

    const processing = documents.filter((doc) =>
      PROCESSING_STATUSES.includes(doc.status)
    ).length;
    const ready = documents.filter((doc) => doc.status === "READY").length;
    const failed = documents.filter((doc) => doc.status === "FAILED").length;

    return { processing, ready, failed, total: documents.length };
  }, [documents]);

  // Start polling
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current || !enabled) return;

    isPollingRef.current = true;
    pollingIntervalRef.current = setInterval(() => {
      onRefetch();
    }, pollingInterval);
  }, [onRefetch, pollingInterval, enabled]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  // Effect to manage polling based on document status
  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    const shouldPoll = hasProcessingDocuments();

    if (shouldPoll && !isPollingRef.current) {
      startPolling();
    } else if (!shouldPoll && isPollingRef.current) {
      stopPolling();
    }
  }, [enabled, hasProcessingDocuments, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isPolling: isPollingRef.current && hasProcessingDocuments(),
    hasProcessingDocuments: hasProcessingDocuments(),
    processingCounts: getProcessingCounts(),
    stopPolling,
  };
};

export default useDocumentProcessingPolling;
